import type { CSSProperties, FC, ReactNode } from 'react'
import type { CradleController, CradleReadout } from '@/components/landing/cradle-scene'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Hand, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Accent, SectionHeading } from '@/components/landing/section-heading'

const CradleScene = dynamic(() => import('@/components/landing/cradle-scene'), { ssr: false })

const STRING_LENGTH = 0.2

const planets = [
    { key: 'moon', label: "Moon", gravity: 1.62 },
    { key: 'mars', label: "Mars", gravity: 3.71 },
    { key: 'earth', label: "Earth", gravity: 9.81 },
    { key: 'jupiter', label: "Jupiter", gravity: 24.79 }
] as const

type PlanetKey = typeof planets[number]['key']

const stateLabels: Record<CradleReadout['state'], string> = {
    swinging: "Swinging",
    holding: "Holding",
    rest: "At rest"
}

export const LabSection: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const controllerRef = useRef<CradleController | null>(null)
    const energyRef = useRef<HTMLSpanElement>(null)
    const energyBarRef = useRef<HTMLSpanElement>(null)
    const stateRef = useRef<HTMLSpanElement>(null)
    const [mounted, setMounted] = useState(false)
    const [planet, setPlanet] = useState<PlanetKey>('earth')
    const [elasticity, setElasticity] = useState(0.98)
    const [releaseAngle, setReleaseAngle] = useState(0.72)

    const gravity = planets.find(item => item.key === planet)?.gravity ?? 9.81
    const period = 2 * Math.PI * Math.sqrt(STRING_LENGTH / gravity)
    const impactSpeed = Math.sqrt(2 * gravity * STRING_LENGTH * (1 - Math.cos(releaseAngle)))
    const elasticityProgress = ((elasticity - 0.7) / 0.3) * 100

    useEffect(() => {
        const stage = stageRef.current

        if (!stage) return

        if (typeof IntersectionObserver === 'undefined') {
            setMounted(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setMounted(true)
                observer.disconnect()
            }
        }, { rootMargin: '600px 0px' })

        observer.observe(stage)

        return () => observer.disconnect()
    }, [])

    const handleReadout = useCallback((readout: CradleReadout) => {
        const percent = Math.round(readout.energy * 100)

        if (energyRef.current) energyRef.current.textContent = `${percent}%`
        if (energyBarRef.current) energyBarRef.current.style.width = `${percent}%`
        if (stateRef.current) stateRef.current.textContent = stateLabels[readout.state]
    }, [])

    const handleRelease = useCallback((angle: number) => {
        setReleaseAngle(angle)
    }, [])

    return (
        <section id='lab' className='scroll-mt-20 py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        index="02"
                        label="The lab"
                        title={<>Momentum, <Accent>conserved.</Accent></>}
                        description="Grab a sphere, pull it back and let go. Then take the whole experiment to the Moon or Jupiter and watch the swing stretch out or snap tight."
                    />
                </Reveal>

                <Reveal className='mt-12'>
                    <div className='grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]'>
                        <div className='relative h-[400px] overflow-hidden rounded-[32px] border border-border/80 bg-white elevate sm:h-[480px] lg:h-auto lg:min-h-[600px]'>
                            <div
                                aria-hidden='true'
                                className='absolute inset-0 bg-grid opacity-70 [mask-image:radial-gradient(ellipse_at_50%_55%,black_25%,transparent_78%)]'
                            />

                            <div ref={stageRef} className='absolute inset-0'>
                                {mounted && (
                                    <CradleScene
                                        gravity={gravity}
                                        elasticity={elasticity}
                                        controllerRef={controllerRef}
                                        onReadout={handleReadout}
                                        onRelease={handleRelease}
                                    />
                                )}
                            </div>

                            <p className='pointer-events-none absolute left-5 top-4 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground'>
                                Fig. 2.1 / Newton&apos;s cradle
                            </p>

                            <p className='pointer-events-none absolute right-4 top-3.5 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-white/85 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-ink backdrop-blur'>
                                <span className='size-1.5 animate-pulse rounded-full bg-brand' />
                                <span ref={stateRef}>Ready</span>
                            </p>

                            <p className='pointer-events-none absolute bottom-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-border/70 bg-white/85 px-3.5 py-1.5 text-xs text-muted-foreground backdrop-blur'>
                                <Hand className='size-4 text-ink' />
                                Drag a sphere sideways, then let go
                            </p>
                        </div>

                        <aside className='flex flex-col gap-7 rounded-[32px] border border-border/80 bg-white p-6 elevate sm:p-7'>
                            <div>
                                <div className='flex items-baseline justify-between'>
                                    <h3 id='gravity-label' className='text-sm font-medium text-ink'>
                                        Gravity
                                    </h3>

                                    <span className='font-mono text-sm tabular-nums text-ink'>
                                        {gravity.toFixed(2)} m/s²
                                    </span>
                                </div>

                                <div role='group' aria-labelledby='gravity-label' className='mt-3 grid grid-cols-4 gap-1 rounded-2xl bg-secondary p-1'>
                                    {planets.map(item => (
                                        <button
                                            key={item.key}
                                            type='button'
                                            aria-pressed={planet === item.key}
                                            onClick={() => setPlanet(item.key)}
                                            className={cn(
                                                'h-10 rounded-xl text-[13px] font-medium transition-all',
                                                planet === item.key ? 'bg-white text-ink shadow-[0_1px_2px_rgba(13,14,20,0.08),0_4px_12px_-4px_rgba(13,14,20,0.18)]' : 'text-muted-foreground hover:text-ink'
                                            )}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className='flex items-baseline justify-between'>
                                    <label htmlFor='elasticity' className='text-sm font-medium text-ink'>
                                        Elasticity
                                    </label>

                                    <span className='font-mono text-sm tabular-nums text-ink'>
                                        e = {elasticity.toFixed(2)}
                                    </span>
                                </div>

                                <input
                                    id='elasticity'
                                    type='range'
                                    min={0.7}
                                    max={1}
                                    step={0.01}
                                    value={elasticity}
                                    onChange={event => setElasticity(Number(event.target.value))}
                                    aria-valuetext={`Coefficient of restitution ${elasticity.toFixed(2)}`}
                                    className='range mt-3'
                                    style={{ '--range-progress': `${elasticityProgress}%` } as CSSProperties}
                                />

                                <div className='mt-1 flex justify-between text-[11px] text-muted-foreground'>
                                    <span>Soft clay</span>
                                    <span>Hardened steel</span>
                                </div>
                            </div>

                            <div>
                                <h3 className='text-sm font-medium text-ink'>
                                    Release
                                </h3>

                                <div className='mt-3 grid grid-cols-[1fr_1fr_1fr_auto] gap-2'>
                                    {[1, 2, 3].map(count => (
                                        <button
                                            key={count}
                                            type='button'
                                            onClick={() => controllerRef.current?.lift(count)}
                                            className='h-11 rounded-xl border border-border bg-white text-[13px] font-medium text-ink transition-colors hover:border-ink/25 hover:bg-secondary'
                                        >
                                            Lift {count}
                                        </button>
                                    ))}

                                    <button
                                        type='button'
                                        aria-label="Reset the cradle"
                                        title="Reset"
                                        onClick={() => controllerRef.current?.reset()}
                                        className='flex size-11 items-center justify-center rounded-xl border border-border bg-white text-ink transition-colors hover:border-ink/25 hover:bg-secondary'
                                    >
                                        <RotateCcw className='size-4' />
                                    </button>
                                </div>
                            </div>

                            <dl className='grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border'>
                                <div className='bg-white p-4'>
                                    <dt className='text-[11px] text-muted-foreground'>Period</dt>
                                    <dd className='mt-1 font-mono text-lg tabular-nums text-ink'>{period.toFixed(2)} s</dd>
                                </div>

                                <div className='bg-white p-4'>
                                    <dt className='text-[11px] text-muted-foreground'>Impact speed</dt>
                                    <dd className='mt-1 font-mono text-lg tabular-nums text-ink'>{impactSpeed.toFixed(2)} m/s</dd>
                                </div>

                                <div className='bg-white p-4'>
                                    <dt className='text-[11px] text-muted-foreground'>Energy kept</dt>
                                    <dd className='mt-1 font-mono text-lg tabular-nums text-ink'>
                                        <span ref={energyRef}>100%</span>
                                    </dd>
                                    <dd className='mt-2 h-1 overflow-hidden rounded-full bg-secondary'>
                                        <span ref={energyBarRef} className='block h-full w-full rounded-full bg-brand transition-[width] duration-200' />
                                    </dd>
                                </div>

                                <div className='bg-white p-4'>
                                    <dt className='text-[11px] text-muted-foreground'>String length</dt>
                                    <dd className='mt-1 font-mono text-lg tabular-nums text-ink'>0.20 m</dd>
                                </div>
                            </dl>

                            <div className='mt-auto rounded-2xl bg-secondary/80 p-5'>
                                <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground'>
                                    Live equation
                                </p>

                                <p className='mt-2 font-serif text-[28px] leading-none text-ink'>
                                    <span className='italic'>T</span> = 2π √(<span className='italic text-brand'>L</span> / <span className='italic text-signal'>g</span>)
                                </p>

                                <p className='mt-3 font-mono text-xs tabular-nums text-muted-foreground'>
                                    = 2π √(<span className='text-brand'>{STRING_LENGTH.toFixed(2)}</span> / <span className='text-signal'>{gravity.toFixed(2)}</span>) = <span className='text-ink'>{period.toFixed(2)} s</span>
                                </p>
                            </div>
                        </aside>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
